import { useEffect, forwardRef } from '@wordpress/element';
import { pointer, select } from 'd3';
import { getHiddenColorScale, getPOI, getPOIProjectedCoords } from './helpers';
const HiddenCanvas = forwardRef((props, ref) => {
	const {
		dimensions,
		path,
		projection,
		features,
		onSelect,
		setSelected,
		selected,
		setTooltipData,
		zoomTrans,
		POIs,
		POISize,
		data,
		zoom,
	} = props;

	useEffect(() => {
		select(ref.current).call(zoom);

		draw();
	}, [dimensions, zoomTrans]);

	function onMouseMove(e) {
		e.preventDefault();

		const [mouseX, mouseY] = pointer(e);

		const mouseFeats = getMouseFeatures(mouseX, mouseY);

		if (mouseFeats?.length) {
			setTooltipData({
				features: mouseFeats,
				left: `${mouseX}px`,
				top: `${mouseY}px`,
			});
			ref.current.style.cursor = 'pointer';
		} else {
			ref.current.style.cursor = 'initial';
			setTooltipData();
		}
	}

	function getMouseFeatures(mouseX, mouseY) {
		if (
			mouseX > 0 &&
			mouseX < dimensions.width &&
			mouseY > 0 &&
			mouseY < dimensions.height
		) {
			const hiddenContext = ref.current.getContext('2d', {
				willReadFrequently: true,
			});
			const mouseColorData = hiddenContext.getImageData(
				mouseX,
				mouseY,
				1,
				1
			).data;
			const colorKey = `rgb(${mouseColorData[0]}, ${mouseColorData[1]}, ${mouseColorData[2]})`;
			const feature = colorToFeature[colorKey];
			const closePOIs = Object.values(colorToFeature).filter((f) => {
				if (!feature || !(f.coordinates && feature.coordinates))
					return false;

				const distance = calculateDistance(
					f.coordinates[0],
					f.coordinates[1],
					feature.coordinates[0],
					feature.coordinates[1]
				);
				// 100km
				return distance < 100000;
			});
			if (closePOIs?.length) return closePOIs;
			if (feature) return [feature];
			return [];
		}
		return [];
	}

	// Ref: http://www.movable-type.co.uk/scripts/latlong.html
	function calculateDistance(lat1, lon1, lat2, lon2) {
		const R = 6371e3; // metres
		const φ1 = (lat1 * Math.PI) / 180; // φ, λ in radians
		const φ2 = (lat2 * Math.PI) / 180;
		const Δφ = ((lat2 - lat1) * Math.PI) / 180;
		const Δλ = ((lon2 - lon1) * Math.PI) / 180;

		const a =
			Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
			Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

		const d = R * c; // in metres
		return d;
	}

	function onMouseDown(e) {
		e.preventDefault();
		if (!onSelect) return;

		const [mouseX, mouseY] = pointer(e);
		const mouseFeats = getMouseFeatures(mouseX, mouseY);
		if (!selected) {
			if (mouseFeats?.length) {
				triggerSelect(mouseFeats);
			}
		} else if (
			selected &&
			mouseFeats &&
			JSON.stringify(mouseFeats) !== JSON.stringify(selected)
		) {
			triggerSelect(mouseFeats);
		} else {
			triggerSelect([]);
		}
	}

	function triggerSelect(feature) {
		onSelect(feature);
		setSelected(feature);
	}

	const colorToFeature = {};
	function draw() {
		if (!ref.current) return;
		const context = ref.current.getContext('2d', {
			willReadFrequently: true,
		});
		context.save();

		context.clearRect(0, 0, dimensions.width, dimensions.height);

		path.context(context);

		const { x, y, k } = zoomTrans;
		context.translate(x, y);
		context.scale(k, k);

		const hiddenColorScale = getHiddenColorScale(data.length);
		features.forEach((f, i) => {
			const fill = hiddenColorScale(i);
			colorToFeature[fill] = f;

			context.beginPath();
			// Draw land
			path(f);

			context.fillStyle = fill;

			context.fill();
		});

		POIs.forEach((p, i) => {
			const POI = getPOI(p, data);

			if (!(POI && POI.coordinates)) {
				return;
			}
			const [coorX, coorY] = getPOIProjectedCoords(POI, projection);

			context.beginPath();
			const rectSize = (POISize * k) / k;

			const fill = hiddenColorScale(features.length + i);
			context.fillStyle = fill;
			colorToFeature[fill] = { ...POI, ...p };
			context.fillRect(coorX, coorY, rectSize, rectSize);
		});
		context.restore();
	}
	draw();

	return (
		<canvas
			className="hidden-canvas"
			ref={ref}
			width={dimensions.width}
			height={dimensions.height}
			onMouseMove={onMouseMove}
			onClick={onMouseDown}
			onMouseLeave={onMouseMove}
			style={{
				position: 'absolute',
				top: '0',
				left: '0',
				opacity: 0,
			}}
		/>
	);
});
export default HiddenCanvas;
